import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Shield, Lock, Eye, Database, CreditCard, Mail, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';

const PrivacyPolicyInner = () => {
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Header */}
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors mb-8"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-black text-white">
                  <PageEditable path="hero.title" as="span" fallback="Privacy Policy" />
                </h1>
                <p className="text-gray-400 mt-2">
                  <PageEditable path="hero.lastUpdatedLabel" as="span" fallback="Last Updated:" /> <PageEditable path="hero.lastUpdated" as="span" fallback={lastUpdated} />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg prose-invert max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-300 text-lg leading-relaxed">
              Casa Samba ("we," "our," or "us") is an Afro-Brasilian cultural school of drumming, dance, and culture history, committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong className="text-white">casasamba.com</strong> and use our services, including purchasing products, enrolling in courses, and subscribing to memberships.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              Please read this Privacy Policy carefully. By using our website and services, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">1. Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Personal Information</h3>
            <p className="text-gray-300">We may collect personal information that you voluntarily provide, including:</p>
            <ul className="text-gray-300 space-y-2">
              <li>Name (first and last)</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing address</li>
              <li>Payment information (processed securely through Stripe/PayPal)</li>
              <li>Emergency contact information (for class enrollment)</li>
              <li>Digital signatures (for membership agreements)</li>
            </ul>

            <h3 className="text-xl font-semibold text-amber-400 mt-6 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-300">When you visit our website, we may automatically collect:</p>
            <ul className="text-gray-300 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">2. How We Use Your Information</h2>
            </div>
            <p className="text-gray-300">We use the information we collect to:</p>
            <ul className="text-gray-300 space-y-2">
              <li>Process transactions and send related information (confirmations, receipts, invoices)</li>
              <li>Provide, maintain, and improve our services</li>
              <li>Manage your account and membership</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Protect against fraudulent or unauthorized transactions</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">3. Payment Processing</h2>
            </div>
            <p className="text-gray-300">
              We use third-party payment processors, including <strong className="text-white">Stripe</strong> and <strong className="text-white">PayPal</strong>, to handle payment transactions. We do not store your full credit card information on our servers.
            </p>
            <p className="text-gray-300 mt-4">
              When you make a purchase, your payment information is transmitted directly to our payment processors using industry-standard encryption (SSL/TLS). These processors have their own privacy policies governing the use of your information:
            </p>
            <ul className="text-gray-300 space-y-2 mt-4">
              <li>Stripe Privacy Policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">stripe.com/privacy</a></li>
              <li>PayPal Privacy Policy: <a href="https://www.paypal.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">paypal.com/privacy</a></li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">4. Information Sharing</h2>
            </div>
            <p className="text-gray-300">We may share your information in the following circumstances:</p>
            <ul className="text-gray-300 space-y-2">
              <li><strong className="text-white">Service Providers:</strong> With third parties who perform services on our behalf (payment processing, email delivery, hosting)</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law or to respond to legal process</li>
              <li><strong className="text-white">Protection:</strong> To protect our rights, privacy, safety, or property</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-gray-300 mt-4">
              <strong className="text-amber-400">We do not sell your personal information to third parties.</strong>
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">5. Data Security</h2>
            </div>
            <p className="text-gray-300">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal data</li>
            </ul>
            <p className="text-gray-300 mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">6. Cookies and Tracking</h2>
            <p className="text-gray-300">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small files stored on your device that help us:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li>Remember your preferences and settings</li>
              <li>Keep you logged in</li>
              <li>Understand how you use our website</li>
              <li>Improve our services</li>
            </ul>
            <p className="text-gray-300 mt-4">
              You can control cookies through your browser settings. Disabling cookies may affect the functionality of our website.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
            <p className="text-gray-300">Depending on your location, you may have the right to:</p>
            <ul className="text-gray-300 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To exercise these rights, please contact us at the email address below.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">8. Children's Privacy</h2>
            <p className="text-gray-300">
              Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-white m-0">10. Contact Us</h2>
            </div>
            <p className="text-gray-300">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-6 mt-4">
              <p className="text-white font-bold text-lg">Escola de Samba Casa Samba</p>
              <p className="text-gray-400">New Orleans, Louisiana</p>
              <p className="text-amber-400 mt-2">Email: info@casasamba.com</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Links */}
      <div className="border-t border-stone-800 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-6">
          <Link to="/terms-of-service" className="text-amber-400 hover:text-amber-300 transition-colors">
            Terms of Service
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            Home
          </Link>
          <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function PrivacyPolicy() {
  return (
    <PageEditProvider slug="privacy-policy">
      <PrivacyPolicyInner />
      <PageEditToolbar />
    </PageEditProvider>
  );
}
